import { getUser } from "@synthesis/shared-auth";
import { jwtDecode } from "jwt-decode";
import { useMemo } from "react";

import { GLOBAL_ROLE, GLOBAL_ROLES } from "./use-user-roles";

type ExtUserGroupAttributes = {
  parentChain?: string[];
  origin?: string[];
  longName?: string[];
  remoteId?: string[];
};

type ExtUserGroup = {
  id: string;
  name: string;
  attributes: ExtUserGroupAttributes;
};

type UserInfoToken = {
  sub: string;
  email: string;
  family_name: string;
  given_name: string;
  middle_name: string;
  name: string;
  "global-role": string[];
  organization?: string;
  user_groups?: string[];
  ext_user_groups?: ExtUserGroup[];
  railway?: string;
};

export type UserInfo = {
  id: string;
  sub: string;
  email: string;
  family_name: string;
  given_name: string;
  middle_name: string;
  name: string;
  "global-role": string[];
  initials: string;
  fullName: string;
  roleLabel: string;
  organization: string;
  departmentLabel: string;
  railwayCandidates: string[];
};

const GLOBAL_ROLE_PREFIXES = ["ВНЕШНЕЕ СОГЛАСОВАНИЕ", "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ"] as const;

const stripGlobalRolePrefix = (role: string): string => {
  for (const prefix of GLOBAL_ROLE_PREFIXES) {
    if (role.startsWith(`${prefix} `)) {
      return role.slice(prefix.length + 1).trim();
    }
  }

  return role;
};

const toSentenceCase = (value: string): string => {
  const words = value.toLowerCase().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return "";
  }

  return words
    .map((word, index) => {
      if (word === "нг") {
        return "НГ";
      }

      if (word === "нтп") {
        return "НТП";
      }

      if (index === 0) {
        return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
      }

      return word;
    })
    .join(" ");
};

const INTERNAL_GLOBAL_ROLES = new Set(Object.keys(GLOBAL_ROLES).filter((role) => role.startsWith("ВНУТРЕННЕЕ")));

const INTERNAL_ORGANIZATION = "ОАО «Российские железные дороги»";

const getInitialsFromName = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.at(0)?.toUpperCase() ?? "")
    .join("");

const getUserInitials = (userInfo: UserInfoToken): string => {
  const lastInitial = userInfo.family_name?.[0]?.toUpperCase();
  const firstInitial = userInfo.given_name?.[0]?.toUpperCase();

  if (lastInitial || firstInitial) {
    return `${lastInitial ?? "-"}${firstInitial ?? "-"}`;
  }

  return getInitialsFromName(userInfo.name) || "—";
};

/** Roles that must not appear in the header meta line — show organization (or nothing) instead. */
const HIDDEN_HEADER_ROLE_LABELS = new Set<string>([
  GLOBAL_ROLE.EXTERNAL_APPLICANT,
  GLOBAL_ROLE.EXTERNAL_WITHOUT_ROLE,
  GLOBAL_ROLE.INTERNAL_WITHOUT_ROLE,
]);

const getUserRoleLabel = (userInfo: UserInfoToken): string => {
  const roles = userInfo["global-role"] ?? [];
  const matchedRole = roles.find((role) => role in GLOBAL_ROLES);
  if (!matchedRole || HIDDEN_HEADER_ROLE_LABELS.has(matchedRole)) {
    return "";
  }

  return toSentenceCase(stripGlobalRolePrefix(matchedRole));
};

const getUserOrganization = (userInfo: UserInfoToken): string => {
  if (userInfo.organization?.trim()) {
    return userInfo.organization.trim();
  }

  const group = userInfo.user_groups?.[0]?.trim();
  if (group) {
    return group;
  }

  const roles = userInfo["global-role"] ?? [];
  if (roles.some((role) => INTERNAL_GLOBAL_ROLES.has(role))) {
    return INTERNAL_ORGANIZATION;
  }

  return "";
};

const getUserDepartmentLabel = (userInfo: UserInfoToken): string => {
  const group = userInfo.ext_user_groups?.[0];
  if (!group) {
    return "";
  }

  const label = group.name?.trim() || group.attributes?.origin?.[0]?.trim() || group.attributes?.longName?.[0]?.trim();

  return label || "";
};

export const useUserInfo = (): UserInfo | null => {
  const user = getUser();
  const token = user?.access_token;
  const profile = user?.profile;

  return useMemo(() => {
    if (!token) {
      return null;
    }

    const decoded = jwtDecode<UserInfoToken>(token);

    const tokenUserInfo: UserInfoToken = {
      sub: decoded.sub ?? profile?.sub ?? "",
      email: decoded.email ?? profile?.email ?? "",
      family_name: decoded.family_name ?? profile?.family_name ?? "",
      given_name: decoded.given_name ?? profile?.given_name ?? "",
      middle_name: decoded.middle_name ?? profile?.middle_name ?? "",
      name: decoded.name ?? profile?.name ?? "",
      "global-role": decoded["global-role"] ?? (profile?.["global-role"] as string[] | undefined) ?? [],
      organization: decoded.organization ?? (profile?.organization as string | undefined),
      user_groups: decoded.user_groups ?? (profile?.user_groups as string[] | undefined),
      ext_user_groups: decoded.ext_user_groups ?? (profile?.ext_user_groups as ExtUserGroup[] | undefined),
      railway: decoded.railway ?? (profile?.railway as string | undefined),
    };

    const userInfo: UserInfo = {
      id: tokenUserInfo.sub,
      sub: tokenUserInfo.sub,
      email: tokenUserInfo.email,
      family_name: tokenUserInfo.family_name,
      given_name: tokenUserInfo.given_name,
      middle_name: tokenUserInfo.middle_name,
      name: tokenUserInfo.name,
      "global-role": tokenUserInfo["global-role"],
      initials: getUserInitials(tokenUserInfo),
      fullName: [tokenUserInfo.family_name, tokenUserInfo.given_name, tokenUserInfo.middle_name].filter(Boolean).join(" ") || "—",
      roleLabel: getUserRoleLabel(tokenUserInfo),
      organization: getUserOrganization(tokenUserInfo),
      departmentLabel: getUserDepartmentLabel(tokenUserInfo),
      railwayCandidates: [
        tokenUserInfo.railway,
        ...(tokenUserInfo.user_groups ?? []),
        ...(tokenUserInfo.ext_user_groups ?? []).flatMap((group) => [
          group.name,
          ...(group.attributes?.origin ?? []),
          ...(group.attributes?.longName ?? []),
          ...(group.attributes?.parentChain ?? []),
        ]),
      ].filter((value): value is string => typeof value === "string" && value.length > 0),
    };

    return userInfo;
  }, [token, profile]);
};
